import { Injectable } from '@angular/core';
import { CanActivate, Router } from '@angular/router'
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AuthApiService } from './auth-api.service'
import { AuthTokenRefreshService } from './auth-token-refresh.service';


@Injectable({
  providedIn: 'root'
})
export class AuthService implements CanActivate {
  env = environment;  

  private loggedIn = new BehaviorSubject<boolean>(false);
  isLoggedIn$ = this.loggedIn.asObservable();
  private signingOut = false;
  private logoutCompleted = false;

  constructor(
    public router: Router,
    private authApi: AuthApiService,
    private tokenRefresh: AuthTokenRefreshService
  ) {
    this.checkLoginStatus();
  } 

  checkLoginStatus() {
    this.loggedIn.next(this.hasValidTokenStored());
  }

  hasValidTokenStored(): boolean {
    return this.isValidTokenStored();
  }

  private isValidTokenStored(): boolean {
    const token = localStorage.getItem('token');
    if (token === null) {
      return false;
    }

    const tokenExpiry = localStorage.getItem('tokenExpires');
    if (!tokenExpiry) {
      return false;
    }

    const expiryUnix = Number(tokenExpiry);
    if (!Number.isNaN(expiryUnix) && expiryUnix > 1_000_000_000) {
      return expiryUnix > Date.now() / 1000 + 30;
    }

    const expiryDate = new Date(tokenExpiry);
    return expiryDate > new Date();
  }

  /** Switch to the external layout before navigating to /logout (avoids double router-outlet mount). */
  beginSignOut(): void {
    this.loggedIn.next(false);
  }

  clearSessionAndRedirect(): void {
    this.loggedIn.next(false);
    localStorage.clear();
    this.router.navigate(['/login']);
  }

  logout() {
    if (this.signingOut) {
      return;
    }
    this.signingOut = true;
    this.loggedIn.next(false);

    this.authApi.logout().subscribe({
      next: () => this.completeLogout(),
      error: () => this.completeLogout(),
    });
  }

  private completeLogout(): void {
    if (this.logoutCompleted) {
      return;
    }
    this.logoutCompleted = true;
    localStorage.clear();
    setTimeout(() => {
      window.location.replace('https://www.dooya.co.za/');
    }, 1300);
  }

  canActivate(): boolean | Observable<boolean> {
    if (this.isValidTokenStored()) {
      this.loggedIn.next(true);
      return true;
    }

    if (!localStorage.getItem('token')) {
      return this.failActivate();
    }

    return this.tokenRefresh.refreshToken().pipe(
      map(() => {
        this.loggedIn.next(true);
        return true;
      }),
      catchError(() => of(this.failActivate()))
    );
  }

  private failActivate(): false {
    this.loggedIn.next(false);
    this.router.navigate(['/login']);
    return false;
  }
}